/* Present the same market-aware estimate as collection cards without changing
   Wishlist Hero's saved items, native price nodes, or add-to-cart callbacks. */
(() => {
  const selector = '.wishlist-hero-list-card-product-price';
  const states = new WeakMap();
  const cache = new Map();
  const queue = [];
  let active = 0;
  let scheduled = false;

  function requestFor(host) {
    const source = host.querySelector('.wishlist-hero-price-update[data-product-link]');
    const variant = source?.id.match(/^wishlist-hero-price-update-(\d+)$/)?.[1];
    const handle = source?.dataset.productLink.match(/\/products\/([a-z0-9-]+)(?:[/?#]|$)/i)?.[1];
    if (!variant || !handle) return null;
    const shop = window.Shopify || {};
    const root = new URL(shop.routes?.root || '/', location.origin);
    if (root.origin !== location.origin) return null;
    const url = new URL(`${root.pathname.replace(/\/?$/, '/')}products/${handle}`, location.origin);
    url.searchParams.set('variant', variant);
    url.searchParams.set('section_id', 'canvoil-wishlist-price');
    if (shop.theme?.role === 'unpublished') url.searchParams.set('preview_theme_id', shop.theme.id);
    const currency = shop.currency?.active || '';
    const country = shop.country || '';
    return { url, variant, currency, country, key: `${url.href}|${currency}|${country}` };
  }

  function pump() {
    while (active < 4 && queue.length) {
      active++;
      const job = queue.shift();
      job().finally(() => { active--; pump(); });
    }
  }

  function load(request) {
    const stored = cache.get(request.key);
    if (stored && Date.now() - stored.time < 60000) return stored.promise;
    const promise = new Promise((resolve, reject) => {
      queue.push(async () => {
        try {
          const response = await fetch(request.url.href, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Wishlist price unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const price = html.querySelector('[data-canvoil-wishlist-price]');
          if (!price || price.dataset.variantId !== request.variant ||
              (request.currency && price.dataset.currency !== request.currency) ||
              (request.country && price.dataset.country !== request.country) ||
              !price.querySelector('.price')) throw new Error('Wishlist price context changed');
          resolve(price);
        } catch (error) { reject(error); }
      });
    });
    cache.set(request.key, { time: Date.now(), promise });
    pump();
    return promise;
  }

  function reset(host) {
    host.removeAttribute('data-canvoil-price-ready');
    host.querySelector('[data-canvoil-wishlist-price]')?.remove();
  }

  function update(host) {
    const request = requestFor(host);
    if (!request) {
      states.delete(host);
      if (host.hasAttribute('data-canvoil-price-ready')) reset(host);
      return;
    }
    const current = states.get(host);
    if (current?.key === request.key) {
      if (current.pending) return;
      if (current.content && host.contains(current.content)) return;
      if (current.failed && Date.now() - current.time < 60000) return;
    }
    reset(host);
    const state = { key: request.key, pending: true, time: Date.now() };
    states.set(host, state);
    load(request).then(price => {
      if (!host.isConnected || states.get(host) !== state || requestFor(host)?.key !== request.key) return;
      state.content = price.cloneNode(true);
      host.append(state.content);
      host.setAttribute('data-canvoil-price-ready', '');
    }).catch(() => {
      // Keep the app's real price if a product, variant, or market cannot be verified.
      state.failed = true;
    }).finally(() => { state.pending = false; });
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      document.querySelectorAll(selector).forEach(update);
    });
  }

  new MutationObserver(schedule).observe(document.body, {
    subtree: true, childList: true, attributes: true,
    attributeFilter: ['id', 'data-product-link']
  });
  document.addEventListener('wishlist-hero-wishlist-updated', schedule);
  document.addEventListener('wishlist-hero-wishlist-show', schedule);
  schedule();
})();
